import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import adminApi from "../../services/adminApi";
import { toast } from "react-toastify";
import {
  FiMail, FiClock, FiCheckCircle, FiAlertCircle, FiSearch,
  FiRefreshCw, FiEye, FiTrash2, FiSend, FiX, FiCheck,
  FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight,
  FiFilter, FiPhone, FiMessageSquare, FiArchive, FiExternalLink,
  FiChevronDown, FiInbox
} from "react-icons/fi";

const STATUS_REPLY_OPTIONS = [
  {
    value: "Resolved",
    label: "Mark as Resolved",
    desc: "Closes the inquiry upon sending",
    icon: FiCheckCircle,
    color: "#15803D",
    bg: "rgba(34,197,94,0.12)",
    border: "rgba(34,197,94,0.3)"
  },
  {
    value: "In Review",
    label: "Mark as In Review",
    desc: "Keep open for ongoing correspondence",
    icon: FiEye,
    color: "#1D4ED8",
    bg: "rgba(59,130,246,0.12)",
    border: "rgba(59,130,246,0.3)"
  },
  {
    value: "Pending",
    label: "Keep as Pending",
    desc: "Keep inquiry in unaddressed state",
    icon: FiClock,
    color: "#B45309",
    bg: "rgba(245,158,11,0.12)",
    border: "rgba(245,158,11,0.3)"
  },
  {
    value: "Archived",
    label: "Mark as Archived",
    desc: "Move directly to archives",
    icon: FiArchive,
    color: "#4B5563",
    bg: "rgba(107,114,128,0.12)",
    border: "rgba(107,114,128,0.3)"
  }
];

const STATUS_CONFIG = {
  Pending: {
    label: "Pending",
    bg: "rgba(245,158,11,0.12)",
    color: "#B45309",
    border: "rgba(245,158,11,0.3)",
    icon: FiClock,
  },
  "In Review": {
    label: "In Review",
    bg: "rgba(59,130,246,0.12)",
    color: "#1D4ED8",
    border: "rgba(59,130,246,0.3)",
    icon: FiEye,
  },
  Resolved: {
    label: "Resolved",
    bg: "rgba(34,197,94,0.12)",
    color: "#15803D",
    border: "rgba(34,197,94,0.3)",
    icon: FiCheckCircle,
  },
  Archived: {
    label: "Archived",
    bg: "rgba(107,114,128,0.12)",
    color: "#4B5563",
    border: "rgba(107,114,128,0.3)",
    icon: FiArchive,
  },
};

const INQUIRY_CATEGORIES = [
  "All",
  "General Inquiry",
  "Class Bookings",
  "Subscriptions & Billing",
  "Teacher Training",
  "Private Coaching",
  "Technical Support"
];

export default function UserQueriesPage() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusBreakdown, setStatusBreakdown] = useState({
    Total: 0,
    Pending: 0,
    InReview: 0,
    Resolved: 0,
    Archived: 0
  });

  // Modal / Detail state
  const [activeQuery, setActiveQuery] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusDraft, setStatusDraft] = useState("Pending");
  const [notesDraft, setNotesDraft] = useState("");
  const [updating, setUpdating] = useState(false);

  // Compose Email Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState({
    subject: "",
    message: "",
    updateStatusTo: "Resolved"
  });
  const [sendingReply, setSendingReply] = useState(false);

  // Custom Lifecycle Status Dropdown in Reply Modal
  const [replyStatusDropdownOpen, setReplyStatusDropdownOpen] = useState(false);
  const replyStatusDropdownRef = useRef(null);

  // Custom Topic / Category Filter Dropdown
  const [topicDropdownOpen, setTopicDropdownOpen] = useState(false);
  const topicDropdownRef = useRef(null);

  // Custom Delete Confirmation Modal State
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    query: null,
    isDeleting: false,
  });

  const selectedReplyStatus = useMemo(() => {
    return STATUS_REPLY_OPTIONS.find((s) => s.value === replyDraft.updateStatusTo) || STATUS_REPLY_OPTIONS[0];
  }, [replyDraft.updateStatusTo]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (replyStatusDropdownRef.current && !replyStatusDropdownRef.current.contains(e.target)) {
        setReplyStatusDropdownOpen(false);
      }
      if (topicDropdownRef.current && !topicDropdownRef.current.contains(e.target)) {
        setTopicDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Fetch queries from admin API
  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getQueries({
        search: search.trim(),
        status: statusFilter,
        inquiryType: categoryFilter === "All" ? "" : categoryFilter,
        page,
        pageSize
      });

      if (res.success && res.data) {
        const raw = res.data;
        const items = raw.items || raw.Items || [];
        setQueries(items);
        setTotalCount(raw.totalCount ?? raw.TotalCount ?? 0);
        setTotalPages(raw.totalPages ?? raw.TotalPages ?? 1);
        
        const sb = raw.statusBreakdown || raw.StatusBreakdown;
        if (sb) {
          setStatusBreakdown({
            Total: sb.total ?? sb.Total ?? 0,
            Pending: sb.pending ?? sb.Pending ?? 0,
            InReview: sb.inReview ?? sb.InReview ?? 0,
            Resolved: sb.resolved ?? sb.Resolved ?? 0,
            Archived: sb.archived ?? sb.Archived ?? 0
          });
        }
      }
    } catch (err) {
      console.error("Failed to load user queries:", err);
      toast.error(err.message || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, categoryFilter, page, pageSize]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  // Open detail modal and mark as read
  const handleOpenDetail = async (query) => {
    setActiveQuery(query);
    setStatusDraft(query.status || "Pending");
    setNotesDraft(query.adminNotes || "");
    setModalOpen(true);

    if (!query.isRead) {
      try {
        await adminApi.getQuery(query.id);
        setQueries(prev => prev.map(q => q.id === query.id ? { ...q, isRead: true } : q));
      } catch (err) {
        console.warn("Failed to mark query read:", err);
      }
    }
  };

  // Save status & admin notes
  const handleSaveStatus = async () => {
    if (!activeQuery) return;
    setUpdating(true);
    try {
      const res = await adminApi.updateQueryStatus(activeQuery.id, {
        status: statusDraft,
        adminNotes: notesDraft
      });

      if (res.success) {
        toast.success("Query updated successfully!");
        setQueries(prev => prev.map(q => q.id === activeQuery.id ? {
          ...q,
          status: statusDraft,
          adminNotes: notesDraft,
          resolvedAt: statusDraft === "Resolved" ? new Date().toISOString() : q.resolvedAt
        } : q));
        setActiveQuery(prev => prev ? { ...prev, status: statusDraft, adminNotes: notesDraft } : null);
        fetchQueries();
      }
    } catch (err) {
      toast.error(err.message || "Failed to update query status");
    } finally {
      setUpdating(false);
    }
  };

  // Open Compose Email Reply Modal
  const handleOpenReply = (query, e) => {
    if (e) e.stopPropagation();
    setActiveQuery(query);
    const category = query.inquiryType || "General Inquiry";
    setReplyDraft({
      subject: `Divyyog Support: Regarding your inquiry on ${category}`,
      message: `Namaste ${query.name},\n\nThank you for reaching out to Divyyog regarding "${category}".\n\n\n\nWarm regards,\nDivyyog Support & Acharya Team`,
      updateStatusTo: "Resolved"
    });
    setReplyModalOpen(true);
  };

  // Dispatch Email Reply to User
  const handleSendReply = async () => {
    if (!activeQuery) return;
    if (!replyDraft.subject.trim()) {
      toast.error("Please enter a subject for the email.");
      return;
    }
    if (!replyDraft.message.trim()) {
      toast.error("Please enter a reply message.");
      return;
    }

    setSendingReply(true);
    try {
      const res = await adminApi.replyToQuery(activeQuery.id, {
        subject: replyDraft.subject.trim(),
        message: replyDraft.message.trim(),
        updateStatusTo: replyDraft.updateStatusTo
      });

      if (res.success) {
        toast.success(`Reply email sent successfully to ${activeQuery.email}!`);
        setReplyModalOpen(false);
        fetchQueries();
        if (res.data) {
          setActiveQuery(res.data);
          setStatusDraft(res.data.status);
          setNotesDraft(res.data.adminNotes || "");
        }
      } else {
        toast.error(res.message || "Failed to send email reply.");
      }
    } catch (err) {
      console.error("Reply Error:", err);
      toast.error(err.message || "Error sending email reply.");
    } finally {
      setSendingReply(false);
    }
  };

  // Quick resolve shortcut
  const handleQuickResolve = async (query, e) => {
    e.stopPropagation();
    try {
      const res = await adminApi.updateQueryStatus(query.id, {
        status: "Resolved",
        adminNotes: query.adminNotes || "Resolved via quick action"
      });
      if (res.success) {
        toast.success(`Inquiry #${query.id} marked as Resolved!`);
        fetchQueries();
      }
    } catch (err) {
      toast.error(err.message || "Failed to resolve inquiry.");
    }
  };

  // Custom Delete Inquiry Handlers
  const promptDelete = (query, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setDeleteConfirmModal({
      isOpen: true,
      query,
      isDeleting: false,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmModal.query) return;
    const q = deleteConfirmModal.query;
    setDeleteConfirmModal((prev) => ({ ...prev, isDeleting: true }));
    try {
      const res = await adminApi.deleteQuery(q.id);
      if (res.success) {
        toast.success("Inquiry deleted successfully!");
        if (activeQuery?.id === q.id) {
          setModalOpen(false);
          setActiveQuery(null);
        }
        setDeleteConfirmModal({ isOpen: false, query: null, isDeleting: false });
        fetchQueries();
      } else {
        toast.error(res.message || "Failed to delete inquiry.");
        setDeleteConfirmModal((prev) => ({ ...prev, isDeleting: false }));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete inquiry.");
      setDeleteConfirmModal((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const metricCards = useMemo(() => {
    const total = statusBreakdown.Total || 0;
    const pending = statusBreakdown.Pending || 0;
    const inReview = statusBreakdown.InReview || 0;
    const resolved = statusBreakdown.Resolved || 0;

    const calcPct = (count) => {
      if (!total || total === 0) return "0%";
      return `${Math.round((count / total) * 100)}%`;
    };

    return [
      {
        id: "all",
        filterKey: "all",
        tag: "GLOBAL INBOX",
        label: "Total Inquiries",
        count: total,
        pct: "100%",
        desc: "All submitted contact forms",
        statusHint: "All Direct Channels",
        icon: FiInbox,
        accentColor: "#F29A0C",
        iconBg: "linear-gradient(135deg, #FFF3E0, #FFE0B2)",
        tagBg: "bg-[#FFF3E0]",
        tagBorder: "border-[#F4D9B5]",
        tagColor: "text-[#D97706]",
        dotColor: "bg-[#22C55E]",
        topShimmer: "from-transparent via-[#F29A0C] to-transparent",
        hoverShadow: "hover:shadow-[0_20px_50px_rgba(242,154,12,0.18)]",
        activeBorder: "border-[#F29A0C] ring-2 ring-[#F29A0C]/40 shadow-[0_15px_40px_rgba(242,154,12,0.22)]"
      },
      {
        id: "pending",
        filterKey: "Pending",
        tag: "AWAITING ACTION",
        label: "Pending Action",
        count: pending,
        pct: calcPct(pending),
        desc: "Unanswered inquiries requiring reply",
        statusHint: "Requires Response",
        icon: FiAlertCircle,
        accentColor: "#D97706",
        iconBg: "linear-gradient(135deg, #FEF3C7, #FDE68A)",
        tagBg: "bg-amber-50",
        tagBorder: "border-amber-200",
        tagColor: "text-amber-700",
        dotColor: "bg-amber-500",
        topShimmer: "from-transparent via-amber-500 to-transparent",
        hoverShadow: "hover:shadow-[0_20px_50px_rgba(245,158,11,0.18)]",
        activeBorder: "border-amber-500 ring-2 ring-amber-500/40 shadow-[0_15px_40px_rgba(245,158,11,0.22)]"
      },
      {
        id: "in_review",
        filterKey: "In Review",
        tag: "IN PROGRESS",
        label: "In Review",
        count: inReview,
        pct: calcPct(inReview),
        desc: "Active correspondence & follow-up",
        statusHint: "Open Conversations",
        icon: FiEye,
        accentColor: "#2563EB",
        iconBg: "linear-gradient(135deg, #EFF6FF, #DBEAFE)",
        tagBg: "bg-blue-50",
        tagBorder: "border-blue-200",
        tagColor: "text-blue-700",
        dotColor: "bg-blue-500",
        topShimmer: "from-transparent via-blue-500 to-transparent",
        hoverShadow: "hover:shadow-[0_20px_50px_rgba(59,130,246,0.18)]",
        activeBorder: "border-blue-500 ring-2 ring-blue-500/40 shadow-[0_15px_40px_rgba(59,130,246,0.22)]"
      },
      {
        id: "resolved",
        filterKey: "Resolved",
        tag: "COMPLETED",
        label: "Resolved",
        count: resolved,
        pct: calcPct(resolved),
        desc: "Addressed & closed inquiries",
        statusHint: "100% Handled",
        icon: FiCheckCircle,
        accentColor: "#16A34A",
        iconBg: "linear-gradient(135deg, #F0FDF4, #DCFCE7)",
        tagBg: "bg-emerald-50",
        tagBorder: "border-emerald-200",
        tagColor: "text-emerald-700",
        dotColor: "bg-emerald-500",
        topShimmer: "from-transparent via-emerald-500 to-transparent",
        hoverShadow: "hover:shadow-[0_20px_50px_rgba(34,197,94,0.18)]",
        activeBorder: "border-emerald-500 ring-2 ring-emerald-500/40 shadow-[0_15px_40px_rgba(34,197,94,0.22)]"
      }
    ];
  }, [statusBreakdown]);

  return (
    <AdminDashboardLayout activeNav="queries">
      <div className="space-y-7 pb-16">
        
        {/* ── Top Header ────────────────────────────────────────────────────────── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/30 text-xs font-bold text-[#F29A0C] uppercase tracking-wider mb-2">
              <FiMail />
              <span>Contact Inquiries Portal</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
              User Queries &amp; Inquiries
            </h1>
            <p className="text-xs sm:text-sm text-[#666666] font-medium mt-1">
              Live submissions from the public website contact form with direct email response &amp; status tracking.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fetchQueries()}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#F4D9B5] text-xs font-bold text-[#333333] hover:text-[#F29A0C] hover:border-[#F29A0C] shadow-sm transition-all duration-300 cursor-pointer disabled:opacity-50"
            >
              <FiRefreshCw className={loading ? "animate-spin text-[#F29A0C]" : "text-[#F29A0C]"} size={13} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* ── High-Impact 3D Metric Summary Cards (Interactive Real-Time Filters) ── */}
        <div className="ad-kpi grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {metricCards.map((card, idx) => {
            const isActive = statusFilter.toLowerCase() === card.filterKey.toLowerCase();
            const Icon = card.icon;

            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06, duration: 0.4 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setStatusFilter(card.filterKey);
                  setPage(1);
                }}
                className={`ad-kpi-card group relative overflow-hidden rounded-[28px] border transition-all duration-300 p-6 flex flex-col justify-between cursor-pointer backdrop-blur-3xl ${
                  isActive
                    ? `${card.activeBorder} bg-white ring-offset-2 ring-offset-[#FFF8EE]`
                    : "border-[#F4D9B5]/80 hover:border-[#F29A0C] bg-gradient-to-b from-white/95 via-white/90 to-[#FFFDF9]/85 shadow-[0_12px_35px_rgba(0,0,0,0.03)]"
                } ${card.hoverShadow}`}
              >
                {/* Top dynamic shimmer accent line */}
                <div
                  className={`absolute top-0 left-0 right-0 h-[2.5px] bg-gradient-to-r ${card.topShimmer} transition-opacity duration-300 ${
                    isActive ? "opacity-100" : "opacity-40 group-hover:opacity-100"
                  }`}
                />

                {/* Dynamic ambient orb bloom in background */}
                <div
                  className={`ad-kpi-orb absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl pointer-events-none transition-all duration-700 ${
                    isActive ? "opacity-35 scale-125" : "opacity-10 group-hover:opacity-30 group-hover:scale-125"
                  }`}
                  style={{ background: card.accentColor }}
                />

                <div className="ad-kpi-body">
                  {/* Glowing Insignia Box & Status Badge */}
                  <div className="ad-kpi-iconrow flex items-center justify-between gap-2 mb-4">
                    <div
                      className="ad-kpi-icon w-13 h-13 rounded-2xl flex items-center justify-center border border-white/80 shadow-[0_4px_16px_rgba(0,0,0,0.08)] group-hover:scale-110 group-hover:rotate-2 transition-all duration-300 shrink-0"
                      style={{ background: card.iconBg, color: card.accentColor }}
                    >
                      <Icon size={22} />
                    </div>

                    <span
                      className={`ad-kpi-note text-[9px] font-extrabold tracking-[1.5px] uppercase px-2.5 py-1 rounded-full border ${card.tagBg} ${card.tagBorder} ${card.tagColor} transition-all`}
                    >
                      {card.tag}
                    </span>
                  </div>

                  {/* Real Dynamic Number & Proportion Pill */}
                  <div className="ad-kpi-iconrow flex items-baseline justify-between gap-2">
                    <h3
                      className="ad-kpi-val text-3xl sm:text-4xl font-black text-[#111111] font-poppins leading-none tracking-tight group-hover:text-[#F29A0C] transition-colors duration-300"
                    >
                      {card.count.toLocaleString()}
                    </h3>
                    <span className="ad-kpi-note text-[11px] font-bold text-[#888888] bg-black/5 px-2 py-0.5 rounded-full">
                      {card.pct}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h4 className="ad-kpi-lbl text-sm sm:text-[15px] font-bold text-[#1F1F1F] mt-2.5 group-hover:text-[#F29A0C] transition-colors">
                    {card.label}
                  </h4>
                  <p className="ad-kpi-note text-[#666666] text-xs font-normal leading-relaxed mt-1">
                    {card.desc}
                  </p>
                </div>

                {/* Footer Live Indicator & Interactive Filter Trigger */}
                <div className="ad-kpi-note flex items-center justify-between mt-4 pt-3 border-t border-[#F4D9B5]/40 text-[11px]">
                  <div className="flex items-center gap-1.5 font-medium text-[#666666]">
                    <span className={`w-2 h-2 rounded-full ${card.dotColor} ${card.count > 0 ? "animate-pulse" : "opacity-40"}`} />
                    <span>{card.statusHint}</span>
                  </div>
                  <span
                    className={`font-bold text-[10.5px] uppercase tracking-wider transition-colors ${
                      isActive ? "text-[#F29A0C]" : "text-[#999999] group-hover:text-[#111111]"
                    }`}
                  >
                    {isActive ? "Filtered Active" : "Click to Filter →"}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Filters & Search Control Bar ─────────────────────────────────────── */}
        <div className="p-5 sm:p-6 rounded-[28px] bg-white/90 border border-[#F4D9B5] shadow-sm backdrop-blur-xl space-y-4">
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            
            {/* Search Input */}
            <div className="relative flex items-center flex-1 max-w-lg h-[46px] rounded-full border border-[#F4D9B5] bg-[#FFF8EE] px-4 focus-within:bg-white focus-within:border-[#F29A0C] transition-all shadow-inner">
              <FiSearch className="text-[#F29A0C] text-sm shrink-0" />
              <input
                type="text"
                placeholder="Search by user name, email, phone, message..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent border-none outline-none text-xs sm:text-sm text-[#111111] font-semibold px-3 placeholder:text-[#888888]"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="text-[#999999] hover:text-[#111111] text-xs p-1 bg-transparent border-none cursor-pointer"
                >
                  <FiX />
                </button>
              )}
            </div>

            {/* Custom Category Dropdown Filter */}
            <div className="relative" ref={topicDropdownRef}>
              <button
                type="button"
                onClick={() => setTopicDropdownOpen((prev) => !prev)}
                aria-expanded={topicDropdownOpen}
                className={`h-[44px] px-4 rounded-full border text-xs font-bold transition-all flex items-center gap-2.5 cursor-pointer shadow-2xs ${
                  categoryFilter !== "All"
                    ? "border-[#F29A0C] bg-[#FFF2E0] text-[#E97818]"
                    : "border-[#F4D9B5] bg-[#FFF8EE] text-[#222222] hover:bg-white hover:border-[#F29A0C]"
                }`}
              >
                <FiFilter className="text-[#F29A0C] text-sm shrink-0" />
                <span className="text-[11px] font-bold text-[#888888] uppercase tracking-wider hidden sm:inline">Topic:</span>
                <span className="truncate max-w-[140px] sm:max-w-[180px]">{categoryFilter}</span>
                <motion.span
                  animate={{ rotate: topicDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="inline-flex text-[#888888]"
                >
                  <FiChevronDown size={14} />
                </motion.span>
              </button>

              <AnimatePresence>
                {topicDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: "easeOut" }}
                    className="absolute right-0 top-[calc(100%+6px)] w-60 sm:w-64 bg-white rounded-2xl border border-[#F4D9B5] shadow-[0_16px_36px_rgba(0,0,0,0.12)] p-1.5 space-y-1 z-40 max-h-72 overflow-y-auto"
                  >
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#999999] border-b border-[#F4D9B5]/40">
                      Filter by Topic
                    </div>
                    {INQUIRY_CATEGORIES.map((cat) => {
                      const isSelected = categoryFilter === cat;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setCategoryFilter(cat);
                            setPage(1);
                            setTopicDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-between transition-all cursor-pointer border-none ${
                            isSelected
                              ? "bg-[#FFF2E0] text-[#E97818] font-bold"
                              : "bg-transparent text-[#333333] hover:bg-[#FFF8EE] hover:text-[#111111]"
                          }`}
                        >
                          <span>{cat}</span>
                          {isSelected && <FiCheck className="text-[#E97818] shrink-0 text-sm" />}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>

          {/* Status Tabs */}
          <div className="ad-scroll flex flex-wrap items-center gap-2 pt-2 border-t border-[#F4D9B5]/40">
            {["all", "Pending", "In Review", "Resolved", "Archived"].map((st) => {
              const active = statusFilter.toLowerCase() === st.toLowerCase();
              const count = st === "all" ? statusBreakdown.Total :
                st === "Pending" ? statusBreakdown.Pending :
                st === "In Review" ? statusBreakdown.InReview :
                st === "Resolved" ? statusBreakdown.Resolved : statusBreakdown.Archived;

              return (
                <button
                  key={st}
                  onClick={() => {
                    setStatusFilter(st);
                    setPage(1);
                  }}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer border-none outline-none flex items-center gap-2 ${
                    active
                      ? "bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white shadow-md scale-102"
                      : "bg-[#FFF8EE] text-[#555555] hover:bg-white hover:text-[#111111] border border-[#F4D9B5]"
                  }`}
                >
                  <span className="capitalize">{st === "all" ? "All Inquiries" : st}</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-extrabold ${
                    active ? "bg-black/25 text-white" : "bg-white text-[#F29A0C] border border-[#F4D9B5]"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

        </div>

        {/* ── Queries Stream List / Table ───────────────────────────────────────── */}
        <div className="rounded-[28px] bg-white border border-[#F4D9B5] shadow-sm overflow-hidden">
          
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <BrandLoader text="Loading user inquiries..." />
            </div>
          ) : queries.length === 0 ? (
            <div className="py-20 px-6 text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] flex items-center justify-center text-2xl text-[#F29A0C] mx-auto">
                <FiMail />
              </div>
              <h3 className="text-base font-bold text-[#111111]">No inquiries found</h3>
              <p className="text-xs text-[#777777] max-w-sm mx-auto">
                {search || statusFilter !== "all" || categoryFilter !== "All"
                  ? "Try adjusting your search terms or filters to find what you're looking for."
                  : "All inquiries submitted through the Contact Us form will appear here automatically."}
              </p>
            </div>
          ) : (
            <div className="ad-table overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#FFF8EE]/70 border-b border-[#F4D9B5]/60 text-[11px] font-bold text-[#666666] uppercase tracking-wider">
                    <th className="py-4 px-5">Visitor / Sadhaka</th>
                    <th className="py-4 px-4">Inquiry Category</th>
                    <th className="py-4 px-4">Message Snippet</th>
                    <th className="py-4 px-4">Submitted At</th>
                    <th className="py-4 px-4">Status</th>
                    <th className="py-4 px-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F4D9B5]/40 text-xs">
                  {queries.map((q) => {
                    const statusCfg = STATUS_CONFIG[q.status] || STATUS_CONFIG.Pending;
                    const StatusIcon = statusCfg.icon;

                    return (
                      <tr 
                        key={q.id}
                        onClick={() => handleOpenDetail(q)}
                        className={`hover:bg-[#FFFBF5] transition-colors cursor-pointer group ${
                          !q.isRead ? "bg-amber-50/40 font-semibold" : ""
                        }`}
                      >
                        {/* User identity */}
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                              {q.name ? q.name.charAt(0).toUpperCase() : "U"}
                            </div>
                            <div>
                              <div className="font-bold text-sm text-[#111111] flex items-center gap-2">
                                <span>{q.name}</span>
                                {!q.isRead && (
                                  <span className="w-2 h-2 rounded-full bg-[#F29A0C] animate-ping" />
                                )}
                              </div>
                              <div className="text-[11px] text-[#666666] font-normal flex items-center gap-2 mt-0.5">
                                <span>{q.email}</span>
                                {q.phone && <span>&bull; {q.phone}</span>}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Inquiry Category */}
                        <td className="py-4 px-4">
                          <span className="inline-block px-3 py-1 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#A25C05] text-[11px] font-bold">
                            {q.inquiryType || "General Inquiry"}
                          </span>
                        </td>

                        {/* Message Preview */}
                        <td className="py-4 px-4 max-w-xs">
                          <p className="line-clamp-2 text-xs text-[#444444] font-normal leading-relaxed">
                            {q.message}
                          </p>
                        </td>

                        {/* Date */}
                        <td className="py-4 px-4 text-[#777777] text-[11px] whitespace-nowrap">
                          {formatDate(q.createdAt)}
                        </td>

                        {/* Status Pill */}
                        <td className="py-4 px-4 whitespace-nowrap">
                          <span 
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border"
                            style={{ 
                              background: statusCfg.bg, 
                              color: statusCfg.color, 
                              borderColor: statusCfg.border 
                            }}
                          >
                            <StatusIcon size={12} />
                            <span>{q.status}</span>
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-5 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            
                            {/* Quick Review */}
                            <button
                              onClick={() => handleOpenDetail(q)}
                              className="w-8 h-8 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] hover:border-[#F29A0C] flex items-center justify-center transition-all cursor-pointer"
                              title="Review Dossier"
                            >
                              <FiEye size={13} />
                            </button>

                            {/* Quick Resolve */}
                            {q.status !== "Resolved" && (
                              <button
                                onClick={(e) => handleQuickResolve(q, e)}
                                className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center transition-all cursor-pointer"
                                title="Mark as Resolved"
                              >
                                <FiCheck size={13} />
                              </button>
                            )}

                            {/* Quick Email Reply (SMTP) */}
                            <button
                              type="button"
                              onClick={(e) => handleOpenReply(q, e)}
                              className="w-8 h-8 rounded-full bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-all cursor-pointer"
                              title="Reply via Email (SMTP)"
                            >
                              <FiMail size={13} />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={(e) => promptDelete(q, e)}
                              className="w-8 h-8 rounded-full bg-rose-50 border border-rose-200 text-rose-500 hover:bg-rose-100 flex items-center justify-center transition-all cursor-pointer"
                              title="Delete Inquiry"
                            >
                              <FiTrash2 size={12} />
                            </button>

                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination — Smart Scalable Suite with Density Selector */}
          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t"
            style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.4)" }}
          >
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-[12px] font-bold text-zinc-500">
                Showing <span className="text-zinc-900">{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to{" "}
                <span className="text-zinc-900">{Math.min(page * pageSize, totalCount)}</span> of{" "}
                <span className="text-zinc-900">{totalCount}</span> inquiries
              </div>

              {/* Rows per page selector */}
              <div className="flex items-center gap-1.5 pl-2 border-l border-zinc-200 text-[11.5px] font-medium text-zinc-500">
                <span>Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="bg-white border border-[#F4D9B5] rounded-lg px-2 py-0.5 text-xs font-bold text-[#111111] outline-none focus:border-[#F29A0C] cursor-pointer shadow-2xs"
                >
                  <option value={6}>6 per page</option>
                  <option value={12}>12 per page</option>
                  <option value={24}>24 per page</option>
                  <option value={48}>48 per page</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Active Page Counter Pill */}
              <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                Page {page} of {Math.max(1, totalPages)}
              </span>

              {/* Jump to First Page Button */}
              <motion.button
                whileTap={{ scale: 0.92 }}
                disabled={page <= 1}
                onClick={() => setPage(1)}
                title="First Page"
                className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
              >
                <FiChevronsLeft size={14} />
              </motion.button>

              {/* Previous Page Button */}
              <motion.button
                whileTap={{ scale: 0.92 }}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                title="Previous Page"
                className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
              >
                <FiChevronLeft size={14} /> Prev
              </motion.button>

              {/* Smart Windowed Page Number Buttons */}
              <div className="flex items-center gap-1">
                {(() => {
                  let pages = [];
                  const maxP = Math.max(1, totalPages);
                  if (maxP <= 7) {
                    pages = Array.from({ length: maxP }, (_, i) => i + 1);
                  } else if (page <= 4) {
                    pages = [1, 2, 3, 4, 5, "...", maxP];
                  } else if (page >= maxP - 3) {
                    pages = [1, "...", maxP - 4, maxP - 3, maxP - 2, maxP - 1, maxP];
                  } else {
                    pages = [1, "...", page - 1, page, page + 1, "...", maxP];
                  }

                  return pages.map((item, idx) => {
                    if (item === "...") {
                      return (
                        <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                          •••
                        </span>
                      );
                    }

                    const isActive = page === item;
                    return (
                      <motion.button
                        key={item}
                        whileTap={{ scale: 0.92 }}
                        onClick={() => setPage(item)}
                        disabled={maxP <= 1}
                        className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                          isActive
                            ? "text-white shadow-xs"
                            : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                        }`}
                        style={
                          isActive
                            ? {
                                background: "linear-gradient(135deg, #F29A0C, #E97818)",
                                borderColor: "transparent",
                                boxShadow: "0 4px 12px rgba(242, 154, 12, 0.35)",
                              }
                            : {}
                        }
                      >
                        {item}
                      </motion.button>
                    );
                  });
                })()}
              </div>

              {/* Next Page Button */}
              <motion.button
                whileTap={{ scale: 0.92 }}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                title="Next Page"
                className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
              >
                Next <FiChevronRight size={14} />
              </motion.button>

              {/* Jump to Last Page Button */}
              <motion.button
                whileTap={{ scale: 0.92 }}
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
                title="Last Page"
                className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
              >
                <FiChevronsRight size={14} />
              </motion.button>
            </div>
          </div>

        </div>

      </div>

      {/* ── Detail Review Modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {modalOpen && activeQuery && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModalOpen(false)}
              className="fixed inset-0 bg-black/65 backdrop-blur-md"
            />

            {/* Dialog Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-2xl bg-white rounded-[32px] border-2 border-[#F4D9B5] shadow-[0_25px_80px_rgba(0,0,0,0.35)] z-10 p-6 sm:p-8 text-[#111111] max-h-[90vh] overflow-y-auto space-y-6"
            >
              {/* Header */}
              <div className="flex items-start justify-between border-b border-[#F4D9B5]/50 pb-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white flex items-center justify-center font-bold text-lg shadow-md">
                    {activeQuery.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#111111]">
                      {activeQuery.name}
                    </h3>
                    <p className="text-xs text-[#666666]">
                      Submitted on {formatDate(activeQuery.createdAt)}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setModalOpen(false)}
                  className="w-9 h-9 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] hover:border-[#F29A0C] flex items-center justify-center text-sm cursor-pointer transition-all border-none"
                >
                  <FiX />
                </button>
              </div>

              {/* Contact Info Pills */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                
                <div className="p-3 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5]/60 flex items-center gap-2.5">
                  <FiMail className="text-[#F29A0C] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-bold text-[#888888]">Email Address</div>
                    <a href={`mailto:${activeQuery.email}`} className="text-xs font-bold text-[#111111] truncate block no-underline hover:text-[#F29A0C]">
                      {activeQuery.email}
                    </a>
                  </div>
                </div>

                <div className="p-3 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5]/60 flex items-center gap-2.5">
                  <FiPhone className="text-[#F29A0C] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-bold text-[#888888]">Phone Number</div>
                    <div className="text-xs font-bold text-[#111111] truncate">
                      {activeQuery.phone || "Not provided"}
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5]/60 flex items-center gap-2.5">
                  <FiFilter className="text-[#F29A0C] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-bold text-[#888888]">Inquiry Topic</div>
                    <div className="text-xs font-bold text-[#111111] truncate">
                      {activeQuery.inquiryType || "General"}
                    </div>
                  </div>
                </div>

              </div>

              {/* Message Body */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#555555]">
                  Full Inquiry Message
                </label>
                <div className="p-5 rounded-2xl bg-[#FFFBF5] border border-[#F4D9B5] text-xs sm:text-sm text-[#222222] font-medium leading-relaxed whitespace-pre-wrap shadow-inner">
                  &ldquo;{activeQuery.message}&rdquo;
                </div>
              </div>

              {/* Status Selector & Notes */}
              <div className="space-y-4 pt-2 border-t border-[#F4D9B5]/50">
                
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#555555] mb-2">
                    Inquiry Lifecycle Status
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {["Pending", "In Review", "Resolved", "Archived"].map((st) => {
                      const cfg = STATUS_CONFIG[st];
                      const isSelected = statusDraft === st;
                      const Icon = cfg.icon;

                      return (
                        <button
                          key={st}
                          type="button"
                          onClick={() => setStatusDraft(st)}
                          className={`p-3 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                            isSelected
                              ? "border-2 shadow-sm scale-102"
                              : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"
                          }`}
                          style={isSelected ? {
                            borderColor: cfg.color,
                            background: cfg.bg,
                            color: cfg.color
                          } : {}}
                        >
                          <Icon size={13} />
                          <span>{st}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#555555] mb-2">
                    Staff Internal Notes
                  </label>
                  <textarea
                    rows={3}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Add internal resolution notes, who called, or actions taken..."
                    className="w-full p-3.5 rounded-2xl border border-[#F4D9B5] bg-[#FFF8EE] text-xs font-medium text-[#111111] outline-none focus:border-[#F29A0C] focus:bg-white transition-all resize-none shadow-inner"
                  />
                </div>

              </div>

              {/* Footer Actions */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[#F4D9B5]/50">
                <div className="flex items-center gap-2">
                  {/* Primary Email Reply Button */}
                  <button
                    type="button"
                    onClick={() => handleOpenReply(activeQuery)}
                    className="px-4 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#E97818] text-white text-xs font-bold hover:shadow-md transition-all flex items-center gap-2 cursor-pointer shadow-xs border-none"
                    title="Send direct email to user"
                  >
                    <FiMail size={13} />
                    <span>Reply via Email</span>
                  </button>

                  {/* Fallback External Mail Client */}
                  <a
                    href={`mailto:${activeQuery.email}?subject=${encodeURIComponent(`Divyyog Support: Regarding your inquiry on ${activeQuery.inquiryType}`)}&body=${encodeURIComponent(`Namaste ${activeQuery.name},\n\nThank you for getting in touch with us at Divyyog regarding "${activeQuery.inquiryType}".\n\nIn response to your query:\n"${activeQuery.message}"\n\n\nWarm regards,\nDivyyog Support & Acharya Team`)}`}
                    className="p-2.5 rounded-full bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition-all flex items-center justify-center no-underline"
                    title="Or open in external mail app"
                  >
                    <FiExternalLink size={13} />
                  </a>

                  <button
                    type="button"
                    onClick={(e) => promptDelete(activeQuery, e)}
                    className="px-4 py-2.5 rounded-full bg-rose-50 border border-rose-200 text-rose-600 text-xs font-bold hover:bg-rose-100 transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <FiTrash2 size={12} />
                    <span>Delete</span>
                  </button>
                </div>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-5 py-2.5 rounded-full border border-gray-300 bg-white text-gray-700 text-xs font-bold hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveStatus}
                    disabled={updating}
                    className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all cursor-pointer border-none flex items-center gap-2"
                  >
                    {updating ? <FiRefreshCw className="animate-spin" /> : <FiCheck />}
                    <span>Save Changes</span>
                  </button>
                </div>

              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Compose Email Reply Modal ─────────────────────────────────── */}
      <AnimatePresence>
        {replyModalOpen && activeQuery && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !sendingReply && setReplyModalOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            />

            {/* Modal Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl border border-[#F4D9B5] shadow-2xl p-6 sm:p-8 z-10 space-y-5 my-8 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-start justify-between border-b border-[#F4D9B5]/60 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#F29A0C] to-[#E97818] text-white flex items-center justify-center text-xl shadow-md">
                    <FiMail />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#111111]">
                      Send Email Reply
                    </h3>
                    <p className="text-xs text-[#666666] flex items-center gap-1.5 mt-0.5">
                      <span>Sending directly to</span>
                      <strong className="text-[#111111]">{activeQuery.email}</strong>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={sendingReply}
                  onClick={() => setReplyModalOpen(false)}
                  className="w-9 h-9 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] flex items-center justify-center text-sm cursor-pointer transition-all border-none"
                >
                  <FiX />
                </button>
              </div>

              {/* Inquiry Summary Context */}
              <div className="p-3.5 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5]/70 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                <div>
                  <span className="text-[#888888] font-medium">Inquirer:</span>{" "}
                  <strong className="text-[#111111]">{activeQuery.name}</strong>
                  <span className="mx-2 text-[#CCCCCC]">•</span>
                  <span className="text-[#888888] font-medium">Topic:</span>{" "}
                  <strong className="text-[#F29A0C]">{activeQuery.inquiryType || "General"}</strong>
                </div>
                <div className="text-[11px] text-[#666666] italic truncate max-w-[280px]">
                  &ldquo;{activeQuery.message}&rdquo;
                </div>
              </div>

              {/* Subject Field */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-[#555555]">
                  Email Subject Line
                </label>
                <input
                  type="text"
                  value={replyDraft.subject}
                  onChange={(e) => setReplyDraft((prev) => ({ ...prev, subject: e.target.value }))}
                  placeholder="Subject of the email..."
                  className="w-full p-3.5 rounded-xl border border-[#F4D9B5] bg-[#FFFBF5] text-xs font-semibold text-[#111111] outline-none focus:border-[#F29A0C] focus:bg-white transition-all shadow-inner"
                />
              </div>

              {/* Message Body Field */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#555555]">
                    Reply Message Body
                  </label>
                  <span className="text-[10px] text-[#888888]">
                    Formatted in the royal Divyyog email layout
                  </span>
                </div>
                <textarea
                  rows={7}
                  value={replyDraft.message}
                  onChange={(e) => setReplyDraft((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="Write your response here..."
                  className="w-full p-4 rounded-2xl border border-[#F4D9B5] bg-[#FFFBF5] text-xs sm:text-sm text-[#222222] font-medium leading-relaxed outline-none focus:border-[#F29A0C] focus:bg-white transition-all resize-y shadow-inner"
                />
              </div>

              {/* Customized Lifecycle Status Dropdown */}
              <div className="p-3.5 rounded-2xl bg-[#FFFBF5] border border-[#F4D9B5] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                <div>
                  <div className="font-bold text-[#111111]">Update Status on Send:</div>
                  <div className="text-[11px] text-[#777777]">Automatically transitions the inquiry lifecycle</div>
                </div>

                <div className="relative min-w-[210px]" ref={replyStatusDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setReplyStatusDropdownOpen((prev) => !prev)}
                    className="w-full px-3 py-2 rounded-xl border bg-white text-xs font-bold flex items-center justify-between gap-2 transition-all cursor-pointer shadow-2xs hover:border-[#F29A0C]"
                    style={{
                      borderColor: selectedReplyStatus.border,
                      color: selectedReplyStatus.color,
                      background: selectedReplyStatus.bg
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <selectedReplyStatus.icon size={13} />
                      <span>{selectedReplyStatus.label}</span>
                    </div>
                    <motion.div
                      animate={{ rotate: replyStatusDropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FiChevronDown size={13} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {replyStatusDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 bottom-[calc(100%+8px)] z-50 w-full sm:w-[260px] bg-white rounded-2xl border border-[#F4D9B5] shadow-[0_-12px_30px_rgba(0,0,0,0.12)] p-1.5 space-y-1"
                      >
                        {STATUS_REPLY_OPTIONS.map((opt) => {
                          const isSel = replyDraft.updateStatusTo === opt.value;
                          const Icon = opt.icon;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setReplyDraft((prev) => ({ ...prev, updateStatusTo: opt.value }));
                                setReplyStatusDropdownOpen(false);
                              }}
                              className={`w-full p-2.5 rounded-xl text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                                isSel ? "bg-[#FFF8EE] border border-[#F4D9B5]" : "hover:bg-gray-50 border border-transparent"
                              }`}
                            >
                              <div
                                className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                                style={{ background: opt.bg, color: opt.color }}
                              >
                                <Icon size={12} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-xs font-bold" style={{ color: opt.color }}>
                                  {opt.label}
                                </div>
                                <div className="text-[10px] text-gray-500 truncate">
                                  {opt.desc}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-[#F4D9B5]/60">
                <button
                  type="button"
                  disabled={sendingReply}
                  onClick={() => setReplyModalOpen(false)}
                  className="px-5 py-2.5 rounded-full border border-gray-300 bg-white text-gray-700 text-xs font-bold hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={sendingReply}
                  onClick={handleSendReply}
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#E97818] text-white text-xs font-bold uppercase tracking-wider shadow-md hover:shadow-lg hover:from-[#E97818] hover:to-[#F29A0C] transition-all cursor-pointer border-none flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sendingReply ? (
                    <>
                      <FiRefreshCw className="animate-spin" size={13} />
                      <span>Sending Email...</span>
                    </>
                  ) : (
                    <>
                      <FiSend size={13} />
                      <span>Send Email Now</span>
                    </>
                  )}
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Custom Inquiry Delete Confirmation Modal ────────────────────────────── */}
      <AnimatePresence>
        {deleteConfirmModal.isOpen && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-md bg-white rounded-[28px] border border-[#F4D9B5] p-6 sm:p-7 shadow-[0_25px_70px_rgba(0,0,0,0.25)] overflow-hidden"
            >
              {/* Top Danger Accent Line */}
              <div className="absolute top-0 left-0 right-0 h-[3.5px] bg-gradient-to-r from-transparent via-red-500 to-transparent" />

              {/* Close Button */}
              <button
                type="button"
                onClick={() => setDeleteConfirmModal({ isOpen: false, query: null, isDeleting: false })}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-500 hover:text-zinc-800 flex items-center justify-center transition-colors cursor-pointer border-none"
              >
                <FiX size={15} />
              </button>

              {/* Header Icon + Title */}
              <div className="flex items-center gap-3.5 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-600 border border-red-200 flex items-center justify-center shrink-0 shadow-[0_4px_12px_rgba(239,68,68,0.15)]">
                  <FiTrash2 size={18} />
                </div>
                <div>
                  <h3 className="text-[17px] font-bold text-zinc-900 leading-snug">
                    Delete Inquiry
                  </h3>
                  <p className="text-[11px] text-zinc-500 font-medium">
                    Permanent Record Deletion
                  </p>
                </div>
              </div>

              {/* Inquiry Preview Box */}
              {deleteConfirmModal.query && (
                <div className="bg-[#FFF8EE] border border-[#F4D9B5]/80 rounded-2xl p-3.5 mb-4">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-bold text-zinc-900 truncate">
                      {deleteConfirmModal.query.name}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border border-[#F4D9B5] text-[#F29A0C] shrink-0">
                      {deleteConfirmModal.query.inquiryType || "General"}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-600 line-clamp-2 leading-relaxed">
                    &ldquo;{deleteConfirmModal.query.message}&rdquo;
                  </p>
                </div>
              )}

              {/* Warning explanation */}
              <p className="text-[12px] text-zinc-600 leading-relaxed mb-6 font-medium">
                Are you sure you want to permanently delete inquiry #{deleteConfirmModal.query?.id}? This action cannot be reversed and will remove this communication log completely.
              </p>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  disabled={deleteConfirmModal.isDeleting}
                  onClick={() => setDeleteConfirmModal({ isOpen: false, query: null, isDeleting: false })}
                  className="px-4 py-2 rounded-full text-[12px] font-bold text-zinc-600 hover:bg-zinc-100 border border-zinc-200 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteConfirmModal.isDeleting}
                  onClick={handleConfirmDelete}
                  className="px-5 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-[12px] shadow-sm hover:shadow-md transition-all cursor-pointer flex items-center gap-1.5 border-none disabled:opacity-60"
                >
                  {deleteConfirmModal.isDeleting ? (
                    <>
                      <FiRefreshCw className="animate-spin" size={12} />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={12} />
                      <span>Confirm Delete</span>
                    </>
                  )}
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </AdminDashboardLayout>
  );
}
